import { type Tier, scaledDelta, scaledRating } from '@topmeeps/shared'

// Compact stat-card sibling to the Gaussian: the border carries the
// tier (its colour) or provisional state (dashed); a big rating number
// and a plays + last-delta line. Sized to sit alongside the curve+axis
// stack and visually defer to it.

const TIER_COLOR: Record<Tier, string> = {
  layperson: 'var(--tier-layperson)',
  novice: 'var(--tier-novice)',
  pupil: 'var(--tier-pupil)',
  student: 'var(--tier-student)',
  scholar: 'var(--tier-scholar)',
  philosopher: 'var(--tier-philosopher)',
  sage: 'var(--tier-sage)',
}

export type RatingSummaryProps = {
  tier: Tier | null
  // Raw μ. Scaled internally for display.
  rawMu: number
  playCount: number
  // Raw Δμ from the most recent play; scaled internally.
  rawDelta?: number
}

export const RatingSummary = ({ tier, rawMu, playCount, rawDelta = 0 }: RatingSummaryProps) => {
  const score = scaledRating(rawMu)
  const delta = scaledDelta(rawDelta)
  const provisional = tier === null

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 6,
        padding: '6px 8px',
        width: 76,
        border: provisional ? '1px dashed var(--line)' : `1px solid ${TIER_COLOR[tier]}`,
        borderRadius: 3,
        fontFamily: 'var(--font-mono)',
        color: 'var(--ink)',
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1 }}>{score}</div>
      <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
        {playCount}p
        {delta !== 0 && (
          <span
            style={{ marginLeft: 4, color: delta > 0 ? 'var(--felt-ink)' : 'var(--terra-ink)' }}
          >
            {delta > 0 ? `+${delta}` : delta}
          </span>
        )}
      </div>
    </div>
  )
}
